/**
 * equalnotequals.com shell — TRANSLATION LAYER ONLY.
 *
 * equalnotequals.com can run this game in a frame with its own chrome over the
 * top: a pill in the corner and an escape menu. None of that chrome is drawn
 * here and none of it ever should be. It belongs to the site, and this game
 * played on its own domain must not sprout it.
 *
 * What this file provides is a headless channel: it declares what the game can
 * do and does what it is told. The shell renders the controls from the schema an
 * ADAPTER supplies, so adding a setting is a change here and nothing over there.
 * Contract: ene_homepage/SHELL.md.
 *
 * It imports nothing on purpose. An earlier version reached straight into
 * `engine/sfx` and `engine/music`, which is fine for a game that has both and
 * wrong for one that has neither — it would have pulled the whole audio stack
 * into a bundle that never plays a sound. The caller knows what it owns; this
 * file does not need to.
 *
 * Everything is inert when not embedded: `inShell` is false on the game's own
 * domain and every branch is skipped.
 */

const NS = "ene.shell/1";
const SHELL = "https://equalnotequals.com";

export interface ShellSetting {
  key: string;
  label: string;
  kind: "range" | "toggle" | "select";
  value: number | boolean | string;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string; label: string }>;
}

export interface ShellAdapter {
  /** What this game lets the shell change. May be empty. */
  settings(): ShellSetting[];
  /** Apply one. Values arrive exactly as declared — ranges as whole numbers. */
  set(key: string, value: number | boolean | string): void;
}

/** Are we inside the shell? Wrapped: reading window.parent can throw. */
export const inShell: boolean = (() => {
  try { return window.parent !== window; } catch { return false; }
})();

const EMPTY: ShellAdapter = { settings: () => [], set: () => {} };
let adapter: ShellAdapter = EMPTY;
let greeted = false;
/* Suppress the unlock we cause ourselves. Opening the shell's menu releases the
   pointer, which fires pointerlockchange — forwarding that as an Escape would
   toggle the menu straight back shut. */
let ignoreUnlockUntil = 0;
let lastEsc = 0;

/** One Escape intent, however it reached us. The keydown and the pointer-unlock
 *  can both describe the same press, and two would cancel each other out. */
function sendEsc(why: string) {
  const now = Date.now();
  if (now - lastEsc < 300) { console.info("[shell] esc ignored (duplicate)", why); return; }
  lastEsc = now;
  console.info("[shell] esc ->", why);
  window.parent.postMessage({ ns: NS, type: "esc" }, SHELL);
}

function hello() {
  if (!inShell) return;
  window.parent.postMessage({ ns: NS, type: "hello", settings: adapter.settings() }, SHELL);
}

/** Tell the shell the player moved one of OUR controls, so its menu stays
 *  honest. Safe to call unconditionally; a no-op outside the shell. */
export function shellChanged(key: string, value: number | boolean | string): void {
  if (!inShell) return;
  window.parent.postMessage({ ns: NS, type: "changed", key, value }, SHELL);
}

export function startShell(a: ShellAdapter = EMPTY): boolean {
  if (!inShell) return false;
  adapter = a;

  window.addEventListener("message", (e: MessageEvent) => {
    // Verify the sender, always. Anything else is a stranger.
    if (e.origin !== SHELL) return;
    const d = e.data as { ns?: string; type?: string; key?: string; value?: unknown; open?: boolean };
    if (!d || d.ns !== NS) return;

    if (d.type === "ready") { greeted = true; hello(); }
    else if (d.type === "set" && d.key != null) {
      adapter.set(d.key, d.value as number | boolean | string);
    } else if (d.type === "menu" && d.open) {
      // The shell's menu is up: give the mouse back, or the player cannot use it.
      ignoreUnlockUntil = Date.now() + 600;
      document.exitPointerLock?.();
    }
  });

  // The shell cannot see this: with a cross-origin frame focused the keypress
  // goes to this document and stops here. Forwarding it is the only way the
  // escape menu is reachable by keyboard.
  window.addEventListener("keydown", (e) => {
    console.info("[shell] keydown", e.key, "locked:", !!document.pointerLockElement);
    if (e.key === "Escape") sendEsc("keydown");
  }, true);

  /* And the keydown above is NOT enough on its own.
     While the pointer is locked the browser keeps Escape for itself — it exits
     the lock and never dispatches the key to the page. So during actual play,
     the press a player means as "open the menu" arrives here only as a lost
     pointer lock. Treating that as the same intent is what makes one press free
     the mouse and open the menu, instead of needing two. */
  document.addEventListener("pointerlockchange", () => {
    const locked = !!document.pointerLockElement;
    console.info("[shell] pointerlockchange, locked:", locked, "suppressed:", Date.now() < ignoreUnlockUntil);
    if (locked) return;
    if (Date.now() < ignoreUnlockUntil) return;
    sendEsc("pointer unlock");
  });

  console.info("[shell] translation layer active; parent =", SHELL);
  hello();
  setTimeout(() => { if (!greeted) hello(); }, 1200);
  return true;
}
